import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Shield, Users, Mail, Phone, Wallet, MessageSquare, RefreshCw, ArrowLeft, Inbox,
  Trash2, Filter, Download, Calculator, Target, TrendingUp,
  Coins, Save, StickyNote, ChevronDown, GitBranch, Eye, MousePointerClick, Send
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => new Date(iso).toLocaleString("it-IT");
const euro = (n) => (Number(n) || 0).toLocaleString("it-IT") + " €";

const isAlleatoLead = (l) => l.tipo === "collaboratore" && /\[(Alleato|Partner segnalatore)/.test(l.messaggio || "");

// Pipeline CRM: 8 fasi (ultima con due esiti)
const STATI = [
  { key: "nuovo", label: "Nuovo", color: "text-sky-400 border-sky-700/50 bg-sky-500/10" },
  { key: "da_contattare", label: "Da contattare", color: "text-amber-400 border-amber-700/50 bg-amber-500/10" },
  { key: "primo_contatto", label: "Primo contatto", color: "text-indigo-300 border-indigo-700/50 bg-indigo-500/10" },
  { key: "in_attesa", label: "In attesa risposta", color: "text-zinc-300 border-zinc-600 bg-zinc-700/30" },
  { key: "qualificato", label: "Qualificato", color: "text-emerald-300 border-emerald-700/50 bg-emerald-500/10" },
  { key: "appuntamento_fissato", label: "Appuntamento fissato", color: "text-teal-300 border-teal-700/50 bg-teal-500/10" },
  { key: "appuntamento_svolto", label: "Appuntamento svolto", color: "text-cyan-300 border-cyan-700/50 bg-cyan-500/10" },
  { key: "cliente", label: "Cliente acquisito", color: "text-emerald-400 border-emerald-600 bg-emerald-600/20" },
  { key: "non_convertito", label: "Non convertito", color: "text-red-400 border-red-800/50 bg-red-500/10" },
];
const statoOf = (l) => l?.stato || "nuovo";
const statoMeta = (k) => STATI.find((s) => s.key === k) || STATI[0];
const statoLabel = (k) => statoMeta(k).label;

function LeadCard({ lead, kind, onUpdate, onDelete }) {
  const isAlleato = isAlleatoLead(lead);
  const [open, setOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(lead.note || "");
  const stato = statoOf(lead);
  const meta = statoMeta(stato);
  const borderCls =
    stato === "cliente" ? "border-emerald-600/50"
      : stato === "non_convertito" ? "border-red-800/40"
      : lead.contattato ? "border-emerald-700/25" : "border-zinc-800 hover:border-zinc-600";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-zinc-900 border p-4 transition-colors ${borderCls}`}
      data-testid={`lead-card-${kind}`}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <h3 className="font-head font-semibold text-lg text-zinc-50 truncate">{lead.nome}</h3>
          {isAlleato && (
            <span className="shrink-0 text-[9px] font-mono uppercase tracking-wider text-yellow-500 border border-yellow-600/50 bg-yellow-500/10 px-1.5 py-0.5" data-testid="badge-alleato">
              ⚑ Alleato
            </span>
          )}
          <span className={`shrink-0 text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 ${meta.color}`} data-testid="badge-stato">
            {meta.label}
          </span>
          {lead.provenienza && (
            <span className="shrink-0 text-[9px] font-mono uppercase tracking-wider text-sky-400 border border-sky-700/50 bg-sky-500/10 px-1.5 py-0.5" data-testid="badge-provenienza">
              {lead.provenienza}
            </span>
          )}
        </div>
        <span className="text-[10px] font-mono text-zinc-600 shrink-0">{fmt(lead.created_at)}</span>
      </div>
      <div className="space-y-1.5 text-sm">
        {lead.email && <p className="flex items-center gap-2 text-zinc-300"><Mail size={14} className="text-yellow-500 shrink-0" /> {lead.email}</p>}
        {lead.telefono
          ? <p className="flex items-center gap-2 text-zinc-300"><Phone size={14} className="text-yellow-500 shrink-0" /> {lead.telefono}</p>
          : <p className="flex items-center gap-2 text-zinc-500"><Phone size={14} className="shrink-0" /> <span className="italic">nessun telefono</span></p>}
        {kind === "cliente" && lead.patrimonio && (
          <p className="flex items-center gap-2 text-zinc-300"><Wallet size={14} className="text-yellow-500 shrink-0" /> {lead.patrimonio}</p>
        )}
        {lead.messaggio && (
          <div className="flex items-start gap-2 text-zinc-400 pt-1 border-t border-zinc-800 mt-2">
            <MessageSquare size={14} className="text-zinc-500 shrink-0 mt-0.5" />
            <span className="italic">{lead.messaggio}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
        <select value={stato} onChange={(e) => onUpdate(lead, { stato: e.target.value })} data-testid="lead-stato-select"
          className="flex-1 min-w-0 bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs py-2 px-2 focus:outline-none focus:border-yellow-500 cursor-pointer">
          {STATI.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button onClick={() => setOpen((v) => !v)} data-testid="lead-notes-toggle"
          className={`flex items-center gap-1 border px-2.5 py-2 text-xs transition-colors ${lead.note ? "border-yellow-600/50 text-yellow-500" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
          <StickyNote size={13} /> <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>
        <button onClick={() => onDelete(lead)} data-testid="delete-lead-button"
          className="flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-red-500 hover:border-red-700/50 px-2.5 py-2 text-xs transition-colors">
          <Trash2 size={13} />
        </button>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-3" data-testid="lead-notes-panel">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Note</p>
            <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={3} data-testid="lead-note-input"
              placeholder="Annota qui dettagli, esigenze, prossimi passi…"
              className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-500 text-sm text-zinc-100 py-2 px-2.5 focus:outline-none resize-y" />
            <button onClick={() => onUpdate(lead, { note: noteDraft })} data-testid="lead-note-save"
              className="mt-2 flex items-center gap-1.5 bg-yellow-500 text-black font-semibold hover:bg-yellow-400 px-3 py-1.5 text-xs rounded-full">
              <Save size={12} /> Salva nota
            </button>
          </div>
          {Array.isArray(lead.stato_storico) && lead.stato_storico.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Storico stati</p>
              <ul className="space-y-1" data-testid="lead-stato-storico">
                {lead.stato_storico.slice().reverse().map((h, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-zinc-400">
                    <span>{statoLabel(h.stato)}</span>
                    <span className="font-mono text-zinc-600">{fmt(h.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

function Column({ title, icon: Icon, leads, kind, empty, onUpdate, onDelete }) {
  return (
    <section className="flex-1">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-head font-bold text-2xl tracking-tight flex items-center gap-2">
          <Icon size={22} className="text-yellow-500" /> {title}
        </h2>
        <span className="font-mono text-sm text-zinc-500" data-testid={`count-${kind}`}>{leads.length}</span>
      </div>
      <div className="space-y-3">
        {leads.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900 p-8 text-center" data-testid={`empty-${kind}`}>
            <Inbox size={26} className="text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">{empty}</p>
          </div>
        ) : leads.map((l) => <LeadCard key={l.id} lead={l} kind={kind} onUpdate={onUpdate} onDelete={onDelete} />)}
      </div>
    </section>
  );
}

const PERIODI = [{ k: "7", l: "7 giorni" }, { k: "30", l: "30 giorni" }, { k: "sempre", l: "Sempre" }];

function MetodoStatsPanel() {
  const [periodo, setPeriodo] = useState("30");
  const [st, setSt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/metodo/stats`, { params: { periodo } }).then(({ data }) => setSt(data)).catch(() => setSt(null));
  }, [periodo]);

  const cards = st ? [
    { l: "Visite pagina", v: st.visite, icon: Eye, id: "visite" },
    { l: "Clic WhatsApp", v: st.whatsapp, sub: `${st.pct_whatsapp}% delle visite`, icon: MessageSquare, id: "whatsapp", accent: "text-emerald-400" },
    { l: "Clic calcolatore", v: st.calcolatore, sub: `${st.pct_calcolatore}% delle visite`, icon: Calculator, id: "calcolatore", accent: "text-yellow-500" },
    { l: "Moduli inviati", v: st.modulo ?? 0, sub: `${st.pct_modulo ?? 0}% delle visite`, icon: Send, id: "modulo", accent: "text-sky-400" },
  ] : [];

  return (
    <div className="mb-8" data-testid="metodo-stats">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MousePointerClick size={16} className="text-yellow-500" />
          <h2 className="font-head font-bold text-lg tracking-tight">Pagina Metodo · Clic e contatti</h2>
        </div>
        <div className="flex border border-zinc-700" data-testid="metodo-stats-periodo">
          {PERIODI.map((p) => (
            <button key={p.k} onClick={() => setPeriodo(p.k)} data-testid={`metodo-periodo-${p.k}`}
              className={`px-3 py-1.5 text-xs transition-colors ${periodo === p.k ? "bg-yellow-500 text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>
              {p.l}
            </button>
          ))}
        </div>
      </div>
      {!st ? (
        <p className="text-sm text-zinc-500" data-testid="metodo-stats-empty">Statistiche non disponibili.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((c) => (
              <div key={c.id} className="bg-zinc-900 border border-zinc-800 p-4" data-testid={`metodo-stat-${c.id}`}>
                <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500"><c.icon size={12} /> {c.l}</p>
                <p className={`font-head font-extrabold text-3xl mt-1 ${c.accent || "text-zinc-100"}`}>{c.v}</p>
                {c.sub && <p className="text-xs text-zinc-500 mt-1">{c.sub}</p>}
              </div>
            ))}
          </div>
          {st.provenienze.length > 0 && (
            <div className="mt-3 bg-zinc-900 border border-zinc-800 overflow-x-auto" data-testid="metodo-stats-provenienze">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 text-left">
                    <th className="px-4 py-2.5 font-normal">Provenienza</th>
                    <th className="px-4 py-2.5 font-normal text-right">Visite</th>
                    <th className="px-4 py-2.5 font-normal text-right">WhatsApp</th>
                    <th className="px-4 py-2.5 font-normal text-right">Calcolatore</th>
                    <th className="px-4 py-2.5 font-normal text-right">Moduli</th>
                  </tr>
                </thead>
                <tbody>
                  {st.provenienze.map((r) => (
                    <tr key={r.provenienza} className="border-t border-zinc-800" data-testid="metodo-prov-row">
                      <td className="px-4 py-2.5 text-zinc-200">{r.provenienza}</td>
                      <td className="px-4 py-2.5 text-right">{r.visita}</td>
                      <td className="px-4 py-2.5 text-right">{r.whatsapp} <span className="text-zinc-500 text-xs">({r.pct_whatsapp}%)</span></td>
                      <td className="px-4 py-2.5 text-right">{r.calcolatore} <span className="text-zinc-500 text-xs">({r.pct_calcolatore}%)</span></td>
                      <td className="px-4 py-2.5 text-right">{r.modulo ?? 0} <span className="text-zinc-500 text-xs">({r.pct_modulo ?? 0}%)</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-zinc-600 mt-2">Conteggio anonimo: ogni dato vale una volta per visita (sessione del browser). Nessun cookie, nessun dato personale, nessun IP salvato: solo totali per giorno e provenienza.</p>
        </>
      )}
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyPending, setOnlyPending] = useState(false);
  const [onlyAlleati, setOnlyAlleati] = useState(false);
  const [statoFilter, setStatoFilter] = useState("tutti");
  const [paStats, setPaStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [savingHist, setSavingHist] = useState(false);
  const [kpi, setKpi] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, statsRes, histRes, kpiRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/piano-azione/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/gold-history`).catch(() => ({ data: null })),
        axios.get(`${API}/funnel/kpi`).catch(() => ({ data: null })),
      ]);
      setLeads(data);
      setPaStats(statsRes.data);
      setHistory(histRes.data);
      setKpi(kpiRes.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updatePoint = (i, key, val) => {
    setHistory((h) => {
      const points = [...h.points];
      points[i] = { ...points[i], [key]: val };
      return { ...h, points };
    });
  };

  const saveHistory = async () => {
    setSavingHist(true);
    try {
      const points = history.points.map((p) => ({
        anno: Number(p.anno), oro_eur_oz: Number(p.oro_eur_oz), inflazione: Number(p.inflazione),
      }));
      const { data } = await axios.put(`${API}/gold-history`, { points });
      setHistory(data);
      toast.success("Dati storici salvati");
    } catch {
      toast.error("Errore nel salvataggio dei dati storici");
    } finally {
      setSavingHist(false);
    }
  };

  const resetPaStats = async () => {
    if (!window.confirm("Azzerare tutte le statistiche del Calcolatore (calcoli effettuati)? I lead NON verranno eliminati.")) return;
    try {
      await axios.delete(`${API}/piano-azione/events`);
      const { data } = await axios.get(`${API}/piano-azione/stats`);
      setPaStats(data);
      toast.success("Statistiche azzerate");
    } catch {
      toast.error("Errore nell'azzeramento");
    }
  };

  const updateLead = async (lead, patch) => {
    const prev = leads;
    setLeads((p) => p.map((l) => (l.id === lead.id ? { ...l, ...patch } : l)));
    try {
      const { data } = await axios.patch(`${API}/leads/${lead.id}`, patch);
      setLeads((p) => p.map((l) => (l.id === lead.id ? data : l)));
      if (patch.stato) toast.success(`Stato: ${statoLabel(patch.stato)}`);
      else if (patch.note !== undefined) toast.success("Nota salvata");
    } catch {
      setLeads(prev);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Eliminare il lead "${lead.nome}"?`)) return;
    const prev = leads;
    setLeads((p) => p.filter((l) => l.id !== lead.id));
    try {
      await axios.delete(`${API}/leads/${lead.id}`);
      toast.success("Lead eliminato");
    } catch {
      setLeads(prev);
      toast.error("Errore nell'eliminazione");
    }
  };

  let visible = onlyPending ? leads.filter((l) => !l.contattato) : leads;
  if (onlyAlleati) visible = visible.filter(isAlleatoLead);
  if (statoFilter !== "tutti") visible = visible.filter((l) => statoOf(l) === statoFilter);
  const clienti = visible.filter((l) => l.tipo === "cliente");
  const collaboratori = visible.filter((l) => l.tipo === "collaboratore");
  const pendingCount = leads.filter((l) => !l.contattato).length;
  const alleatiCount = leads.filter(isAlleatoLead).length;
  const statoCounts = STATI.reduce((acc, s) => { acc[s.key] = leads.filter((l) => statoOf(l) === s.key).length; return acc; }, {});

  const exportCSV = () => {
    if (visible.length === 0) { toast.error("Nessun lead da esportare"); return; }
    const cols = ["tipo", "nome", "email", "telefono", "patrimonio", "provenienza", "stato", "note", "report_capitale", "report_anni", "report_inflazione", "report_valore_reale", "messaggio", "contattato", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const val = (l, c) => c === "contattato" ? (l[c] ? "Sì" : "No") : c === "stato" ? statoLabel(statoOf(l)) : l[c];
    const rows = visible.map((l) => cols.map((c) => esc(val(l, c))).join(","));
    const csv = "\uFEFF" + [cols.join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${visible.length} lead esportati in CSV`);
  };

  return (
    <div className="bg-zinc-950 text-zinc-50 min-h-screen">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-zinc-800 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 bg-zinc-950/95 backdrop-blur z-20">
        <div className="flex items-center gap-3">
          <div className="bg-yellow-500 p-2"><Users size={20} className="text-black" /></div>
          <div>
            <h1 className="font-head font-extrabold text-xl tracking-tight leading-none">LEAD DASHBOARD</h1>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Contatti dal portale</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setOnlyPending((v) => !v)} data-testid="filter-pending-button"
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              onlyPending
                ? "bg-yellow-500 text-black font-semibold"
                : "border border-zinc-700 text-zinc-200 hover:border-yellow-500 hover:text-yellow-500"}`}>
            <Filter size={15} /> Solo da contattare ({pendingCount})
          </button>
          <button onClick={() => setOnlyAlleati((v) => !v)} data-testid="filter-alleati-button"
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              onlyAlleati
                ? "bg-yellow-500 text-black font-semibold"
                : "border border-zinc-700 text-zinc-200 hover:border-yellow-500 hover:text-yellow-500"}`}>
            <Filter size={15} /> Solo Alleati ({alleatiCount})
          </button>
          <button onClick={exportCSV} data-testid="export-csv-button"
            className="flex items-center gap-2 border border-zinc-700 text-zinc-200 hover:border-yellow-500 hover:text-yellow-500 px-4 py-2 text-sm transition-colors">
            <Download size={15} /> Esporta CSV
          </button>
          <button onClick={load} disabled={loading} data-testid="refresh-leads-button"
            className="flex items-center gap-2 border border-zinc-700 text-zinc-200 hover:border-yellow-500 hover:text-yellow-500 px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
          <Link to="/radar" data-testid="link-radar"
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm transition-colors">
            <ArrowLeft size={15} /> Radar
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {kpi && (() => {
          const nd = (v) => (v === null || v === undefined ? "n/d" : v);
          const cards = [
            { l: "Reach IG (28gg)", v: nd(kpi.ig?.reach) },
            { l: "Visite profilo", v: nd(kpi.ig?.profile_views) },
            { l: "Click link bio", v: nd(kpi.ig?.website_clicks) },
            { l: "Avvii calcolatore", v: kpi.calcolatore?.avvii ?? 0 },
            { l: "Report richiesti", v: kpi.calcolatore?.completati ?? 0 },
            { l: "Lead", v: kpi.lead?.totali ?? 0, accent: "text-emerald-400" },
            { l: "% lead raggiunti", v: `${kpi.lead?.pct_raggiunti ?? 0}%` },
            { l: "Tempo 1° contatto", v: kpi.tempo_medio_primo_contatto_ore == null ? "n/d" : `${kpi.tempo_medio_primo_contatto_ore} h`, accent: "text-yellow-500" },
            { l: "Qualificati", v: kpi.lead?.qualificati ?? 0 },
            { l: "Appuntamenti", v: kpi.appuntamenti?.fissati ?? 0 },
            { l: "Clienti", v: kpi.clienti ?? 0, accent: "text-emerald-400" },
          ];
          return (
            <div className="mb-8" data-testid="funnel-kpi">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-yellow-500" />
                <h2 className="font-head font-bold text-lg tracking-tight">Funnel completo · KPI</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {cards.map((c, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 p-3.5" data-testid={`kpi-card-${i}`}>
                    <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500 leading-tight">{c.l}</p>
                    <p className={`font-head font-extrabold text-2xl mt-1 ${c.accent || "text-zinc-100"}`}>{c.v}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-zinc-600 mt-2">Reach/visite/click dall'account Instagram collegato (aggiornati all'ultimo sync; "n/d" se non ancora disponibili). CPL/CPC compariranno con le campagne a pagamento.</p>
            </div>
          );
        })()}

        <MetodoStatsPanel />

        {paStats && (
          <div className="mb-8" data-testid="piano-azione-stats">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calculator size={16} className="text-yellow-500" />
                <h2 className="font-head font-bold text-lg tracking-tight">Calcolatore · Analytics</h2>
              </div>
              <button onClick={resetPaStats} data-testid="reset-pa-stats-button"
                className="flex items-center gap-1.5 border border-zinc-700 text-zinc-400 hover:text-red-500 hover:border-red-700/50 px-3 py-1.5 text-xs transition-colors">
                <Trash2 size={13} /> Azzera statistiche
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-zinc-900 border border-yellow-700/40 p-4" data-testid="pa-stat-calcoli">
                <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                  <Calculator size={12} /> Calcoli effettuati
                </p>
                <p className="font-head font-extrabold text-3xl mt-1 text-yellow-500">{paStats.calcoli}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid="pa-stat-lead">
                <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                  <Target size={12} /> Lead generati
                </p>
                <p className="font-head font-extrabold text-3xl mt-1">{paStats.lead}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid="pa-stat-conversione">
                <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                  <TrendingUp size={12} /> Conversione
                </p>
                <p className={`font-head font-extrabold text-3xl mt-1 ${paStats.conversione >= 20 ? "text-emerald-400" : paStats.conversione >= 8 ? "text-yellow-500" : "text-zinc-100"}`}>
                  {paStats.conversione}%
                </p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid="pa-stat-capitale">
                <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                  <Wallet size={12} /> Capitale medio
                </p>
                <p className="font-head font-extrabold text-2xl mt-1.5">{euro(paStats.capitale_medio)}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid="pa-stat-scenario">
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Scenario più usato</p>
                <p className="font-head font-semibold text-base mt-2 text-zinc-200 leading-tight">{paStats.scenario_top || "—"}</p>
              </div>
            </div>
          </div>
        )}

        {paStats && (
          <div className="mb-8" data-testid="pa-funnel-block">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid="pa-stat-avvii">
                <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">Avvii calcolatore</p>
                <p className="font-head font-extrabold text-2xl text-zinc-100 mt-1">{paStats.avvii ?? 0}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid="pa-stat-tasso">
                <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">Avvio → Report</p>
                <p className="font-head font-extrabold text-2xl text-yellow-500 mt-1">{paStats.tasso_completamento ?? 0}%</p>
              </div>
            </div>
            {Array.isArray(paStats.provenienze) && paStats.provenienze.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 p-4 overflow-x-auto" data-testid="pa-provenienze">
                <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">Provenienza (attribuzione funnel)</p>
                <table className="w-full text-sm min-w-[420px]">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500">
                      <th className="text-left pb-2 pr-3">Fonte</th>
                      <th className="text-right pb-2 pr-3">Avvii</th>
                      <th className="text-right pb-2 pr-3">Report</th>
                      <th className="text-right pb-2">Lead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paStats.provenienze.map((p, i) => (
                      <tr key={i} className="border-t border-zinc-800">
                        <td className="py-1.5 pr-3 text-zinc-200">{p.provenienza}</td>
                        <td className="py-1.5 pr-3 text-right text-zinc-400">{p.avvii}</td>
                        <td className="py-1.5 pr-3 text-right text-zinc-400">{p.completati}</td>
                        <td className="py-1.5 text-right text-emerald-400 font-semibold">{p.lead}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {paStats?.ab_telefono && (
          <div className="mb-8" data-testid="ab-telefono-block">
            <div className="flex items-center gap-2 mb-3">
              <Target size={16} className="text-yellow-500" />
              <h2 className="font-head font-bold text-lg tracking-tight">A/B test telefono</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[["A", "Telefono facoltativo"], ["B", "Telefono richiesto"]].map(([k, desc]) => {
                const d = paStats.ab_telefono[k] || {};
                return (
                  <div key={k} className="bg-zinc-900 border border-zinc-800 p-4" data-testid={`ab-variant-${k}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-head font-bold text-base">Variante {k}</p>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{desc}</span>
                    </div>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      {[["Avvii", d.avvii], ["Report", d.completati], ["Lead", d.lead], ["Qualif.", d.qualificati], ["Appunt.", d.appuntamenti]].map(([lb, vv], i) => (
                        <div key={i}>
                          <p className="font-head font-extrabold text-xl text-zinc-100">{vv ?? 0}</p>
                          <p className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{lb}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className={`text-[11px] mt-2 ${paStats.ab_telefono.soglia_50_raggiunta ? "text-emerald-400" : "text-zinc-600"}`}>
              {paStats.ab_telefono.soglia_50_raggiunta
                ? "✓ Entrambe le varianti hanno ≥50 lead: puoi decidere confrontando appuntamenti fissati e lead qualificati."
                : "Test in corso: continua finché ogni variante ha ≥50 lead. Decidi in base ad appuntamenti fissati e qualificati, non al solo completamento."}
            </p>
          </div>
        )}

        {history?.points && (
          <div className="mb-8" data-testid="gold-history-editor">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Coins size={16} className="text-yellow-500" />
                <h2 className="font-head font-bold text-lg tracking-tight">Dati storici · Oro vs Inflazione (report)</h2>
              </div>
              <button onClick={saveHistory} disabled={savingHist} data-testid="save-history-button"
                className="flex items-center gap-1.5 bg-yellow-500 text-black font-semibold hover:bg-yellow-400 disabled:opacity-60 px-4 py-1.5 text-xs transition-colors rounded-full">
                <Save size={13} /> {savingHist ? "Salvataggio…" : "Salva dati"}
              </button>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-4 overflow-x-auto">
              <table className="w-full text-sm min-w-[460px]">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">
                    <th className="text-left pb-2 pr-3">Anno</th>
                    <th className="text-left pb-2 pr-3">Oro (€/oz, media annua)</th>
                    <th className="text-left pb-2">Inflazione ISTAT (% annuo)</th>
                  </tr>
                </thead>
                <tbody>
                  {history.points.map((p, i) => (
                    <tr key={i} className="border-t border-zinc-800">
                      <td className="py-1.5 pr-3">
                        <input type="number" value={p.anno} onChange={(e) => updatePoint(i, "anno", e.target.value)}
                          className="w-20 bg-zinc-950 border border-zinc-700 focus:border-yellow-500 py-1.5 px-2 text-zinc-100 focus:outline-none" />
                      </td>
                      <td className="py-1.5 pr-3">
                        <input type="number" step="0.01" value={p.oro_eur_oz} onChange={(e) => updatePoint(i, "oro_eur_oz", e.target.value)}
                          className="w-32 bg-zinc-950 border border-zinc-700 focus:border-yellow-500 py-1.5 px-2 text-zinc-100 focus:outline-none" />
                      </td>
                      <td className="py-1.5">
                        <input type="number" step="0.1" value={p.inflazione} onChange={(e) => updatePoint(i, "inflazione", e.target.value)}
                          className="w-28 bg-zinc-950 border border-zinc-700 focus:border-yellow-500 py-1.5 px-2 text-zinc-100 focus:outline-none" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-zinc-600 mt-3">
                Fonti: oro World Gold Council (cambio BCE), inflazione ISTAT (NIC). Modifica i valori e salva per aggiornare il grafico nel report del calcolatore.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Totale</p>
            <p className="font-head font-extrabold text-3xl mt-1" data-testid="stat-total">{leads.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Clienti</p>
            <p className="font-head font-extrabold text-3xl mt-1 text-yellow-500">{clienti.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Collaboratori</p>
            <p className="font-head font-extrabold text-3xl mt-1 text-yellow-500">{collaboratori.length}</p>
          </div>
        </div>

        <div className="mb-8" data-testid="pipeline-summary">
          <div className="flex items-center gap-2 mb-3">
            <GitBranch size={16} className="text-yellow-500" />
            <h2 className="font-head font-bold text-lg tracking-tight">Pipeline</h2>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setStatoFilter("tutti")} data-testid="pipeline-chip-tutti"
              className={`shrink-0 flex items-center gap-1.5 border px-3 py-1.5 text-xs transition-colors ${statoFilter === "tutti" ? "border-yellow-500 bg-yellow-500/10 text-yellow-500" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
              Tutti <b className="font-mono">{leads.length}</b>
            </button>
            {STATI.map((s) => (
              <button key={s.key} onClick={() => setStatoFilter(s.key)} data-testid={`pipeline-chip-${s.key}`}
                className={`shrink-0 flex items-center gap-1.5 border px-3 py-1.5 text-xs transition-colors ${statoFilter === s.key ? "border-yellow-500 bg-yellow-500/10 text-yellow-500" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                {s.label} <b className="font-mono">{statoCounts[s.key]}</b>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          <Column title="Clienti" icon={Shield} leads={clienti} kind="cliente" onUpdate={updateLead} onDelete={remove}
            empty="Nessun cliente ancora. I contatti dal form clienti appariranno qui." />
          <Column title="Collaboratori" icon={Users} leads={collaboratori} kind="collaboratore" onUpdate={updateLead} onDelete={remove}
            empty="Nessuna candidatura ancora. Le candidature partner appariranno qui." />
        </div>
      </main>
    </div>
  );
}
